import React, { useState } from 'react';
import { Upload, Search, Sparkles, Image as ImageIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ImageUploadModal from './ImageUploadModal';
import StockImageModal from './StockImageModal';
import AIImageModal from './AIImageModal';

interface ImageManagerProps {
  currentImageId?: string;
  currentImageUrl?: string;
  onImageChange: (imageData: { id?: string; url: string; data?: string; metadata: any }) => void;
  title: string;
  tags?: string[];
}

const ImageManager: React.FC<ImageManagerProps> = ({
  currentImageId,
  currentImageUrl,
  onImageChange,
  title,
  tags = []
}) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [previousImageData, setPreviousImageData] = useState<{ id?: string; url: string; data?: string; metadata: any } | undefined>(
    currentImageId ? { id: currentImageId, url: `/api/images/serve/${currentImageId}`, metadata: {} } : undefined
  );

  const handleUpload = () => {
    setShowOptions(false);
    setShowUploadModal(true);
  };

  const handleStockSearch = () => {
    setShowOptions(false);
    setShowStockModal(true);
  };

  const handleAIGenerate = () => {
    setShowOptions(false);
    setShowAIModal(true);
  };

  const handleImageUpload = async (imageData: {
    filename: string;
    mimeType: string;
    size: number;
    data: string;
    source: 'upload';
    metadata: any;
  }) => {
    try {
      // Store previous image data for cleanup
      if (currentImageId && currentImageId !== previousImageData?.id) {
        setPreviousImageData({ id: currentImageId, url: `/api/images/serve/${currentImageId}`, metadata: {} });
      }

      // For temporary storage, we'll create a temporary URL and store data in memory
      const tempImageId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const tempUrl = `data:${imageData.mimeType};base64,${imageData.data}`;
      
      // Store image data temporarily (not in database yet)
      const tempImageData = {
        id: tempImageId,
        url: tempUrl,
        data: imageData.data,
        metadata: {
          ...imageData.metadata,
          isTemporary: true,
          previousImageId: previousImageData?.id
        }
      };
      
      onImageChange(tempImageData);
    } catch (error) {
      console.error('Error uploading image:', error);
      // TODO: Show error toast
    }
  };

  const handleStockImageSelect = async (stockImage: any) => {
    try {
      // Store previous image data for cleanup
      if (currentImageId && currentImageId !== previousImageData?.id) {
        setPreviousImageData({ id: currentImageId, url: `/api/images/serve/${currentImageId}`, metadata: {} });
      }

      // For stock images, store temporarily without database
      const tempImageId = `temp-stock-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      
      const tempImageData = {
        id: tempImageId,
        url: stockImage.url,
        metadata: {
          source: 'stock', // Add the source field
          author: stockImage.source === 'pexels' ? 'Pexels' : 'Freepik',
          authorUrl: stockImage.source === 'pexels' ? 'https://www.pexels.com/' : 'https://www.freepik.es/',
          caption: stockImage.source === 'pexels' ? `${t('statistics.image.photoBy')} Pexels` : `${t('statistics.image.designedBy')} Freepik`,
          originalUrl: stockImage.url,
          width: stockImage.width,
          height: stockImage.height,
          alt: stockImage.alt,
          isTemporary: true,
          previousImageId: previousImageData?.id
        }
      };
      
      onImageChange(tempImageData);
      
      // console.log('Stock image selected:', {
        // id: tempImageId,
        // url: stockImage.url,
        // photographer: stockImage.photographer,
        // source: stockImage.source
      // }); // Removed for production
      
    } catch (error) {
      console.error('Error selecting stock image:', error);
      // TODO: Show error toast
    }
  };

  const handleAIImageGenerate = async (imageData: {
    filename: string;
    mimeType: string;
    size: number;
    data: string;
    source: 'ai';
    metadata: any;
  }) => {
    try {
      // Store previous image data for cleanup
      if (currentImageId && currentImageId !== previousImageData?.id) {
        setPreviousImageData({ id: currentImageId, url: `/api/images/serve/${currentImageId}`, metadata: {} });
      }

      // For AI images, store temporarily without database
      const tempImageId = `temp-ai-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const tempUrl = `data:${imageData.mimeType};base64,${imageData.data}`;
      
      const tempImageData = {
        id: tempImageId,
        url: tempUrl,
        data: imageData.data,
        metadata: {
          ...imageData.metadata,
          isTemporary: true,
          previousImageId: previousImageData?.id
        }
      };
      
      onImageChange(tempImageData);
    } catch (error) {
      console.error('Error saving AI image:', error);
      // TODO: Show error toast
    }
  };

  return (
    <div className="relative">
      {/* Current Image */}
      <div className="aspect-video bg-surface-200 rounded-lg overflow-hidden relative group">
        {(currentImageId || currentImageUrl) ? (
          <>
            <img 
              src={currentImageUrl || (currentImageId ? `/api/images/serve/${currentImageId}` : '')} 
              alt={title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <button
                onClick={() => setShowOptions(true)}
                className="opacity-0 group-hover:opacity-100 bg-white/90 text-black px-4 py-2 rounded-lg font-medium transition-opacity"
              >
                {t('common.changeImage')}
              </button>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon size={48} className="mb-4" />
            <p className="text-sm mb-4">{t('common.noImageSelected')}</p>
            <button
              onClick={() => setShowOptions(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Upload size={16} />
              {t('common.addImage')}
            </button>
          </div>
        )}
      </div>

      {/* Image Options Modal */}
      {showOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowOptions(false)}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Gestionar Imagen</h3>
              <button
                onClick={() => setShowOptions(false)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <button
                onClick={handleUpload}
                className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
              >
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Upload size={20} />
                </div>
                <div>
                  <div className="font-medium">Subir archivo</div>
                  <div className="text-sm text-muted-foreground">Seleccionar imagen desde tu dispositivo</div>
                </div>
              </button>

              <button
                onClick={handleStockSearch}
                className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
              >
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <Search size={20} />
                </div>
                <div>
                  <div className="font-medium">Buscar en stock gratuito</div>
                  <div className="text-sm text-muted-foreground">Encontrar imágenes libres de derechos</div>
                </div>
              </button>

              <button
                onClick={handleAIGenerate}
                className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
              >
                <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                  <Sparkles size={20} />
                </div>
                <div>
                  <div className="font-medium">Crear con IA</div>
                  <div className="text-sm text-muted-foreground">Generar imagen personalizada con IA</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ImageUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleImageUpload}
      />

      <StockImageModal
        isOpen={showStockModal}
        onClose={() => setShowStockModal(false)}
        onSelect={handleStockImageSelect}
        initialQuery={title}
        tags={tags}
      />

      <AIImageModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIImageGenerate}
        title={title}
      />
    </div>
  );
};

export default ImageManager; 